/**
 * Hook for server-side Organizations grid data
 *
 * Fetches data from the in-memory search index with pagination, sorting, and filtering.
 * Designed to work with MUI Data Grid Premium server-side mode.
 *
 * Uses React Query for cache management and invalidation.
 * Invalidate with: queryClient.invalidateQueries({ queryKey: ['search', 'organizations'] })
 */

import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-premium'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

const API_BASE = import.meta.env.VITE_API_URL || 'https://grobotic.app:2096'

/** Query key for organizations search - use for invalidation */
export const ORGANIZATIONS_SEARCH_KEY = ['search', 'organizations'] as const

interface SearchParams {
  page: number
  pageSize: number
  sortModel?: GridSortModel
  filterModel?: GridFilterModel
}

interface SearchResult {
  rows: any[]
  totalCount: number
  page: number
  pageSize: number
  aggregations?: {
    price: number
    grossProfit: number
    mediaSpend: number
    netTotal: number
  }
}

interface UseServerSideOrganizationsOptions {
  enabled?: boolean // Toggle server-side mode on/off
  pageSize?: number
  initialSortModel?: GridSortModel
  initialFilterModel?: GridFilterModel
}

interface UseServerSideOrganizationsReturn {
  // Data
  rows: any[]
  totalRowCount: number
  aggregations?: SearchResult['aggregations']

  // Pagination
  page: number
  pageSize: number
  onPageChange: (newPage: number) => void
  onPageSizeChange: (newPageSize: number) => void

  // Sorting
  sortModel: GridSortModel
  onSortModelChange: (newSortModel: GridSortModel) => void

  // Filtering
  filterModel: GridFilterModel
  onFilterModelChange: (newFilterModel: GridFilterModel) => void

  // Loading state
  isLoading: boolean
  error: string | null

  // Refresh
  refresh: () => Promise<void>

  // Server-side mode props for DataGridPremium
  serverSideProps: {
    paginationMode: 'server'
    sortingMode: 'server'
    filterMode: 'server'
    rowCount: number
    paginationModel: { page: number; pageSize: number }
    onPaginationModelChange: (model: { page: number; pageSize: number }) => void
    loading: boolean
  }
}

/**
 * Fetch function for the search API
 */
const fetchOrganizationsSearch = async (params: SearchParams): Promise<SearchResult> => {
  const response = await fetch(`${API_BASE}/search/organizations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Hook for fetching organizations with server-side pagination, sorting, and filtering
 *
 * Uses React Query for caching and invalidation.
 * To refresh data after a mutation, use:
 *   queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_SEARCH_KEY })
 */
export const useServerSideOrganizations = (
  options: UseServerSideOrganizationsOptions = {}
): UseServerSideOrganizationsReturn => {
  const {
    enabled = true,
    pageSize: initialPageSize = 50,
    initialSortModel = [{ field: 'name', sort: 'asc' }],
    initialFilterModel = { items: [], quickFilterValues: [] },
  } = options

  const queryClient = useQueryClient()

  // Controlled state for grid parameters
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel)
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel)

  // Debounced filter model for the query (prevents excessive API calls during typing)
  const [debouncedFilterModel, setDebouncedFilterModel] = useState<GridFilterModel>(initialFilterModel)
  const filterDebounceRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Debounce filter changes
  useEffect(() => {
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current)
    }

    filterDebounceRef.current = setTimeout(() => {
      setDebouncedFilterModel(filterModel)
      setPage(0) // Reset to page 0 when filter changes
    }, 300)

    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current)
      }
    }
  }, [filterModel])

  // Build query key with all search parameters
  const queryKey = [
    ...ORGANIZATIONS_SEARCH_KEY,
    { page, pageSize, sortModel, filterModel: debouncedFilterModel },
  ] as const

  // Use React Query for data fetching
  const {
    data,
    isLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: () =>
      fetchOrganizationsSearch({
        page,
        pageSize,
        sortModel,
        filterModel: debouncedFilterModel,
      }),
    enabled,
    staleTime: 30_000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60_000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    placeholderData: previousData => previousData, // Keep previous data while fetching
  })

  // Extract data from query result
  const rows = data?.rows ?? []
  const totalRowCount = data?.totalCount ?? 0
  const aggregations = data?.aggregations

  /**
   * Handle page change
   */
  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  /**
   * Handle page size change
   */
  const onPageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(0) // Reset to first page
  }, [])

  /**
   * Handle sort model change
   */
  const onSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel)
    setPage(0) // Reset to first page when sorting changes
  }, [])

  /**
   * Handle filter model change
   */
  const onFilterModelChange = useCallback((newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel)
    // Page reset handled in debounce effect
  }, [])

  /**
   * Handle pagination model change (combined page/pageSize from DataGrid)
   */
  const onPaginationModelChange = useCallback((model: { page: number; pageSize: number }) => {
    setPage(model.page)
    setPageSize(model.pageSize)
  }, [])

  /**
   * Manual refresh - invalidates all organization search queries
   */
  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_SEARCH_KEY })
  }, [queryClient])

  // Props to spread onto DataGridPremium for server-side mode
  const serverSideProps = {
    paginationMode: 'server' as const,
    sortingMode: 'server' as const,
    filterMode: 'server' as const,
    rowCount: totalRowCount,
    paginationModel: { page, pageSize },
    onPaginationModelChange,
    loading: isLoading || isFetching,
  }

  return {
    rows,
    totalRowCount,
    aggregations,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    isLoading,
    error: queryError?.message ?? null,
    refresh,
    serverSideProps,
  }
}

/**
 * Check if server-side search is available
 */
export const checkSearchHealth = async (): Promise<{
  available: boolean
  message: string
  documentCount?: number
  lastSyncTime?: string
  isInitialized?: boolean
}> => {
  try {
    const response = await fetch(`${API_BASE}/search/health`)
    const data = await response.json()

    // Extract organizations-specific stats from nested response
    const orgStats = data.organizations || {}

    return {
      available: data.healthy && data.configured,
      message: data.message || (data.healthy ? 'Search available' : 'Search unavailable'),
      // Include sync status fields from organizations index
      documentCount: orgStats.documentCount,
      lastSyncTime: orgStats.lastSyncTime,
      isInitialized: orgStats.isInitialized,
    }
  } catch (err) {
    return {
      available: false,
      message: 'Search service not reachable',
    }
  }
}

/**
 * Trigger a full sync (admin only)
 */
export const triggerFullSync = async (): Promise<{ success: boolean; indexed?: number; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/search/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, error: data.message || 'Sync failed' }
    }

    return { success: true, indexed: data.indexed }
  } catch (err) {
    return { success: false, error: err.message }
  }
}

export default useServerSideOrganizations
